/* The create-or-join fork — STORY-004 — and the workspace switcher — STORY-009.
 *
 * One screen in two states, which is how the prototype drew it: `s-choose` for
 * somebody with nothing yet, `s-switch` for somebody who already belongs
 * somewhere. They differ in their heading and in what is listed above the
 * actions; splitting them would be two screens that must never disagree about
 * how a workspace is entered.
 *
 * ADR-0010 is what makes it exist at all: identity is separate from tenancy, so
 * a User is created with no Organisation and only then chooses — and keeps
 * choosing, because they may hold many.
 *
 * **This screen no longer redirects when there is exactly one Membership.**
 * It did until STORY-009, and that made the drawer's "Switch workspace" a dead
 * loop for the majority case: a Member of one workspace clicking it was sent
 * straight back where they came from, so creating or joining a second one was
 * unreachable from inside the app. "No chooser for a choice of one" is about
 * where SIGNING IN lands, and it lives on the sign-in screen now.
 */

import { formatDate } from '@eventuley/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout } from '../../../shared/ui/index.js';
/* Through auth's public surface, not into its internals — `architecture.md`. */
import { useCurrentUser, useSignOut } from '../../auth/index.js';
import {
  useAcceptInvitation,
  useOwnJoinRequests,
  useReceivedInvitations,
  useSwitchOrganisation,
  useWithdrawJoinRequest,
} from '../api/index.js';

export function ChooseWorkspaceScreen() {
  const t = useTranslate();
  const { data: user } = useCurrentUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { formatLocale } = useI18n();
  const pending = useOwnJoinRequests();
  const withdraw = useWithdrawJoinRequest();
  const switching = useSwitchOrganisation();
  const invited = useReceivedInvitations();
  const acceptInvitation = useAcceptInvitation();
  const waiting = pending.data?.requests ?? [];
  const invitations = invited.data?.invitations ?? [];

  /* The READER's zone here, not a venue's: this is when THEY sent it, and it is
   * the ordinary case `i18n.md` says to be deliberate about rather than the
   * event-time case. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const held = user?.memberships ?? [];
  /* Which state the screen is in. Somebody who already belongs somewhere came
   * to switch; somebody with nothing came to choose for the first time. */
  const switcher = held.length > 0;

  /* Two workspaces may legitimately share a name — two supper clubs, two
   * branches — and the chooser has to stay usable when they do. The id is
   * shown only on the rows where the name is not enough to tell them apart,
   * so the ordinary case is not made uglier for a case that is rare. */
  const ambiguous = new Set(
    held
      .map((membership) => membership.name)
      .filter((name, index, names) => names.indexOf(name) !== index),
  );

  /* Entering is a POST rather than a link, and this is the one place in the app
   * where that is worth the loss of middle-click: the server re-verifies the
   * Membership at the moment of entry and records the switch in the audit
   * trail. A link would navigate first and discover a revoked Membership on the
   * next screen instead. */
  const enter = (organisationId: string) =>
    switching.mutate(organisationId, {
      onSuccess: () =>
        void navigate({
          to: '/orgs/$organisationId/calendar',
          params: { organisationId },
        }),
    });

  /* Every way entering can fail has something to say, not just the interesting
   * one. A session that expired while this screen sat open answers 401, and it
   * is the LIKELIEST failure here: a User with no Membership lands on this
   * screen at every sign-in and may leave it open for hours. Without a branch
   * for it the row click did nothing visible at all. */
  const failure = switching.error;
  const gone = failure instanceof ApiError && failure.status === 404;

  return (
    <AuthScreen
      title={switcher ? t('workspace.switch.title') : t('workspace.choose.title')}
      brand={t('app.name')}
      subtitle={switcher ? t('workspace.switch.body') : t('workspace.choose.body')}
    >
      {/* The workspaces they are already in, FIRST. Without this a returning
          Member had no way into their own workspace at all: sign-in sends
          everybody here, the shell links here as "Switch workspace", and the
          screen offered only ways to make a new one. Only a bookmarked URL
          worked. */}
      {switcher ? (
        <ul className="row-list">
          {held.map((membership) => (
            <li key={membership.organisationId} className="row row--linked">
              <span className="row__main">
                <span className="row__title">
                  {/* A plain button, styled as the row by `.row--linked`: the
                      shared Button component would bring its own chrome and
                      then be reset back out of it. Disabled while any switch is
                      in flight — one at a time, and a second click during the
                      first is a race, not an intention. */}
                  <button
                    type="button"
                    disabled={switching.isPending}
                    onClick={() => enter(membership.organisationId)}
                  >
                    {membership.name}
                  </button>
                </span>
                {/* Role is per Membership — the same User can be an Owner here
                    and a Member next door, and this is where that is decided. */}
                <span className="row__meta">
                  {membership.role === 'owner' ? t('role.owner') : t('role.member')}
                </span>
                {/* Its own element rather than appended to the line above:
                    `i18n.md` forbids building one string out of two translated
                    fragments, and a translator cannot reorder what JavaScript
                    joined with a separator. */}
                {ambiguous.has(membership.name) ? (
                  <span className="row__meta">
                    {t('workspace.switch.disambiguate', { id: membership.organisationId })}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {failure ? (
        <Callout tone="warning">
          {gone
            ? t('workspace.switch.failed')
            : failure instanceof NetworkError
              ? t('error.network')
              : t('error.unexpected')}
        </Callout>
      ) : null}

      {/* The waiting room — STORY-008. Not a confirmation message: a User
          with no Memberships lands here on EVERY sign-in until something is
          approved, so the state has to be on the screen rather than in a flash
          they saw once.

          Nothing here names the workspace. The server answers identically for
          one that does not exist, so copy claiming otherwise would make this
          screen the enumeration oracle the API refused to be — the ID is shown
          because the requester typed it, and nothing else is. */}
      {/* Invitations addressed to this User — STORY-004: "those are listed so
          they are not re-entered by hand". Above the join requests because
          these are actionable NOW, where a request is somebody else's to
          answer; and above the create/join actions for the same reason, since
          a person who was invited has probably come here to accept.

          The workspace NAME is safe to show where a join request's is not: an
          invitation was sent to this address deliberately, so naming the
          workspace tells the recipient nothing they were not already told by
          email. The requests below still show only the id the requester typed,
          which is what keeps that list from being the enumeration oracle the
          API refuses to be. */}
      {invitations.length > 0 ? (
        <>
          <h2>{t('workspace.invited.heading')}</h2>
          <ul className="row-list">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="row">
                <span className="row__main">
                  <span className="row__title">{invitation.organisationName}</span>
                  <span className="row__meta">
                    {t('workspace.invited.expires', {
                      role: invitation.role,
                      when: formatDate(new Date(invitation.expiresAt), {
                        locale: formatLocale,
                        timeZone,
                      }),
                    })}
                  </span>
                </span>
                <Button
                  variant="primary"
                  /* Scoped to this row, like the withdraw button below: one
                     mutation object serves every row, so an unscoped pending
                     flag would spin all of them at once. */
                  busy={
                    acceptInvitation.isPending &&
                    acceptInvitation.variables !== undefined &&
                    'invitationId' in acceptInvitation.variables &&
                    acceptInvitation.variables.invitationId === invitation.id
                  }
                  onClick={() =>
                    acceptInvitation.mutate(
                      { invitationId: invitation.id },
                      {
                        /* Straight in, the way accepting from the emailed link
                           goes. Being told "you have joined" and then left on
                           the chooser is a second click for something already
                           decided. */
                        onSuccess: ({ organisationId }) =>
                          void navigate({
                            to: '/orgs/$organisationId',
                            params: { organisationId },
                          }),
                      },
                    )
                  }
                >
                  {t('workspace.invited.join')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {waiting.length > 0 ? (
        <>
          <h2>{t('workspace.waiting.heading')}</h2>
          <ul className="row-list">
            {waiting.map((request) => (
              <li key={request.id} className="row">
                <span className="row__main">
                  <span className="row__title">
                    <code>{request.organisationId}</code>
                  </span>
                  <span className="row__meta">
                    {t('workspace.waiting.asked', {
                      when: formatDate(new Date(request.requestedAt), {
                        locale: formatLocale,
                        timeZone,
                      }),
                    })}
                  </span>
                </span>
                <span className="badge badge--info">{t('workspace.waiting.status')}</span>
                <Button
                  variant="ghost"
                  /* Scoped to this row: one mutation object serves them all, so
                   an unscoped pending flag would spin every spinner. */
                  busy={withdraw.isPending && withdraw.variables === request.id}
                  onClick={() => withdraw.mutate(request.id)}
                >
                  <span className="sr-only">
                    {t('workspace.waiting.withdraw', { id: request.organisationId })}
                  </span>
                  <span aria-hidden="true">×</span>
                </Button>
              </li>
            ))}
          </ul>
          {/* What waiting COSTS, said plainly. Somebody who has asked and heard
              nothing needs to know they are not half-in, and that starting
              their own is still open to them. */}
          <p className="card__subtitle">{t('workspace.waiting.meanwhile')}</p>
        </>
      ) : null}

      {/* A heading only in the switcher state. With workspaces listed above,
          three unlabelled buttons read as more of the same list rather than as
          the way to somewhere else. */}
      {switcher ? <h2>{t('workspace.switch.another')}</h2> : null}

      <div className="auth__actions">
        {/* A link, not a button: it navigates, and a keyboard or screen-reader
            user should be told so. */}
        <Link to="/next/create" className="btn btn--primary btn--block">
          {t('workspace.choose.create')}
        </Link>

        {/* Built by STORY-007. It was disabled with its reason in text until
            2026-08-18, which is what kept the fork honest while only one of the
            three routes worked. */}
        <Link to="/invitations/accept" className="btn btn--secondary btn--block">
          {t('workspace.choose.join_code')}
        </Link>

        {/* Built by STORY-008. It was visible and disabled with its reason in
            text until 2026-08-19, which is what kept the fork honest while only
            two of the three routes worked. */}
        <Link to="/next/join" className="btn btn--secondary btn--block">
          {t('workspace.choose.join_id')}
        </Link>

        {user ? (
          <Button variant="ghost" block busy={signOut.isPending} onClick={() => signOut.mutate()}>
            {t('workspace.choose.sign_out', { email: user.email })}
          </Button>
        ) : null}
      </div>
    </AuthScreen>
  );
}
